import { ClassType } from 'class-transformer/ClassTransformer';
import { plainToClass } from 'class-transformer';
import { MultiSelectOptionInterface } from '@/components/forms/select/app-select';
import store from '@/store';
import { ListingsStoreGetters } from '@/store/modules/static-listings';
import { format } from 'date-fns';
import { FamilyStatus } from '@/models/relationship.model';
import { RelationshipDataItem, RelationshipItem } from '@/models/base-types';
import { Observable, Subscriber } from 'rxjs';

export function convertToModel<T>(value: T, ModelClass: ClassType<T>): T {
  if (value && typeof value === 'object') {
    return plainToClass(ModelClass, value) as T;
  } else {
    return value;
  }
}

export function convertToModelsArray<T>(values: T[], ModelClass: ClassType<T>): T[] {
  return values?.map((value: T): T => plainToClass(ModelClass, value) as T);
}

export function findInIncludedArray<T>(
  relationship: RelationshipItem,
  includedArray: T[],
  ModelClass: ClassType<T>
): T[] {
  if (Array.isArray(relationship.data)) {
    const resultArray: T[] = (relationship.data as RelationshipDataItem[]).map(
      (item: RelationshipDataItem): T =>
        convertToModel(
          (includedArray as T[]).find((includedArrayItem: any): boolean => includedArrayItem.id === item.id) as T,
          ModelClass
        )
    );

    return resultArray;
  }

  return Object.keys(relationship.data).length
    ? [
        convertToModel(
          (includedArray as T[]).find(
            (includedArrayItem: any): boolean => includedArrayItem.id === (relationship.data as RelationshipDataItem).id
          ) as T,
          ModelClass
        )
      ]
    : [];
}

export function getSelectOptions(storeGetterName: ListingsStoreGetters): MultiSelectOptionInterface[] {
  return store.getters[storeGetterName]
    ? store.getters[storeGetterName].map(
        (item: any): MultiSelectOptionInterface => {
          return {
            label: item.attributes.name,
            value: item.id
          };
        }
      )
    : [];
}

export function getSelectValue(
  arrayOfOptions: MultiSelectOptionInterface[],
  valueId: string
): MultiSelectOptionInterface | null {
  return arrayOfOptions.find((option: MultiSelectOptionInterface): boolean => option.value === valueId) || null;
}

export function getMultiSelectOptions(
  storeGetterName: ListingsStoreGetters,
  searchableArray: any[]
): MultiSelectOptionInterface[] {
  const resultArray: MultiSelectOptionInterface[] = [];

  const defaultOptions: MultiSelectOptionInterface[] = store.getters[storeGetterName]
    ? store.getters[storeGetterName].map(
        (item: any): MultiSelectOptionInterface => {
          return {
            label: item.attributes.name,
            value: item.id
          };
        }
      )
    : [];

  const defaultOptionIds: string[] = defaultOptions.length
    ? defaultOptions.map((option: MultiSelectOptionInterface): string => option.value)
    : [];

  searchableArray.forEach((item: any): void => {
    if (!defaultOptionIds.includes(item.id)) {
      resultArray.push({ value: item.id, label: item.attributes.name });
    }
  });

  return [...defaultOptions, ...resultArray];
}

export function getMultiselectSelectValues(
  arrayOfOptions: MultiSelectOptionInterface[],
  searchableArray: any[]
): MultiSelectOptionInterface[] {
  const valueIds: string[] = searchableArray.map((item: { id: string }): string => item.id);

  return arrayOfOptions.filter((option: MultiSelectOptionInterface): boolean => valueIds.includes(option.value)) || [];
}

export enum SelectTimeFrameOptions {
  thisMonth = 'this month',
  threeMonths = 'within last 3 months',
  sixMonths = 'within last 6 months',
  thisYear = 'this year',
  lastYear = 'last year'
}

export function getDates(value: SelectTimeFrameOptions): [string, string] {
  const now: Date = new Date();
  const currentMonth: number = now.getMonth();
  const currentYear: number = now.getFullYear();
  const currentDay: number = now.getDate();

  let startDate: string = '';
  let endDate: string = '';

  switch (value) {
    case SelectTimeFrameOptions.thisMonth:
      startDate = format(new Date(currentYear, currentMonth, 1), 'yyyy-MM-dd');
      endDate = format(new Date(currentYear, currentMonth, currentDay), 'yyyy-MM-dd');
      return [startDate, endDate];
    case SelectTimeFrameOptions.threeMonths:
      startDate = format(new Date(currentYear, currentMonth - 2, 1), 'yyyy-MM-dd');
      endDate = format(new Date(currentYear, currentMonth, currentDay), 'yyyy-MM-dd');
      return [startDate, endDate];
    case SelectTimeFrameOptions.sixMonths:
      startDate = format(new Date(currentYear, currentMonth - 5, 1), 'yyyy-MM-dd');
      endDate = format(new Date(currentYear, currentMonth, currentDay), 'yyyy-MM-dd');
      return [startDate, endDate];
    case SelectTimeFrameOptions.thisYear:
      startDate = format(new Date(currentYear, 0, 1), 'yyyy-MM-dd');
      endDate = format(new Date(currentYear, currentMonth, currentDay), 'yyyy-MM-dd');
      return [startDate, endDate];
    case SelectTimeFrameOptions.lastYear:
      startDate = format(new Date(currentYear - 1, 0, 1), 'yyyy-MM-dd');
      endDate = format(new Date(currentYear - 1, 11, 31), 'yyyy-MM-dd');
      return [startDate, endDate];
  }
}

export function toTitleCase(value: string): string {
  return value.replace(/(?:^|\s|-)\S/g, (x: string): string => x.toUpperCase());
}

export const selectDateFormatOptions: MultiSelectOptionInterface[] = [
  { label: 'DD/MM/YYYY', value: 'dd/MM/yyyy' },
  { label: 'YYYY/MM/DD', value: 'yyyy/MM/dd' },
  { label: 'MM/DD/YYYY', value: 'MM/dd/yyyy' }
];

export const defaultSelectedDateFormatOptions: MultiSelectOptionInterface = selectDateFormatOptions[0];

export const getOppositeRole: (targetSelectedRols: FamilyStatus) => FamilyStatus = (
  targetSelectedRols: FamilyStatus
): FamilyStatus => {
  switch (targetSelectedRols) {
    // case FamilyStatus.Spouse:
    //   return FamilyStatus.Spouse;
    case FamilyStatus.Partner:
      return FamilyStatus.Partner;
    case FamilyStatus.Child:
      return FamilyStatus.Parent;
    case FamilyStatus.Parent:
      return FamilyStatus.Child;
  }
};

export const isError: (e: any) => boolean = (e: any): boolean => {
  return e && e.stack && e.message;
};

export const trimmingValue: (value?: string | null | undefined) => string = (
  value?: string | null | undefined
): string => (value || '').trim();

export function downloadCSVFile(text: string, fileName: string = 'file.csv'): void {
  const element: HTMLAnchorElement = document.createElement('a');
  element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
  element.setAttribute('download', fileName);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

export function download(href: string, name: string): void {
  const link: HTMLAnchorElement = document.createElement('a');
  link.hidden = true;
  link.href = href;
  link.download = name;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function fileToBase64(file: File): Observable<string> {
  return new Observable<string>((subscriber: Subscriber<string>): void => {
    const reader: FileReader = new FileReader();
    reader.addEventListener(
      'load',
      (): void => {
        subscriber.next(reader.result as string);
        subscriber.complete();
      },
      false
    );
    reader.addEventListener('error', (): void => {
      subscriber.error(reader.error);
    });

    reader.readAsDataURL(file);
  });
}
